import { Controller } from 'stimulus'

// Only fields that are empty or still hold an earlier pick are filled; the SIRET always is.
const HEADERS  = { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
const DEBOUNCE = 300

const t = (key, options) => I18n.t(`company_lookup.${key}`, options)

const escapeHtml = text => String(text ?? '').replace(/[&<>"']/g, char =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char])

const formatSiret = siret => String(siret || '').replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, '$1 $2 $3 $4')

export default class extends Controller {
  static targets = ['siret', 'find', 'panel']

  connect() {
    this.form    = this.element.closest('form')
    this.model   = this.data.get('scope')
    this.refresh = this.refresh.bind(this)
    this.written = new Map()

    // The React address field sets its value without input events.
    ;['input', 'click', 'keyup', 'focusout'].forEach(type => this.form?.addEventListener(type, this.refresh))
    // React mounts the address field after this controller connects.
    this.element.addEventListener('mouseenter', this.refresh)
    this.refresh()
    this.mounted = setTimeout(this.refresh, 500)

    if (this.data.get('suggest') === 'true') this.watchName()
  }

  disconnect() {
    ;['input', 'click', 'keyup', 'focusout'].forEach(type => this.form?.removeEventListener(type, this.refresh))
    this.nameInput?.removeEventListener('input', this.onNameInput)
    this.nameInput?.removeEventListener('keydown', this.onNameKeydown)
    this.element.removeEventListener('mouseenter', this.refresh)
    clearTimeout(this.mounted)
    document.removeEventListener('click', this.onOutsideClick)
    clearTimeout(this.timer)
    this.menu?.remove()
  }

  field(attribute) {
    return this.form?.querySelector(`[name="${this.model}[${attribute}]"]:not([type=hidden])`)
  }

  get nameInput()    { return this.field('name') }
  get addressInput() { return this.field('address') }

  // The new client form also carries a hidden copy of the VAT number.
  get vatInputs() {
    return this.form ? [...this.form.querySelectorAll(`[name="${this.model}[${this.data.get('vat')}]"]`)] : []
  }

  refresh() {
    if (!this.hasFindTarget) return

    const ready = (this.nameInput?.value || '').trim().length >= 3 && (this.addressInput?.value || '').trim().length >= 5
    this.findTarget.disabled = !ready
    this.findTarget.title    = ready ? '' : t('find_hint')
  }

  find(event) {
    event.preventDefault()
    this.request('match', { name: this.nameInput?.value, address: this.addressInput?.value })
        .then(companies => this.showCandidates(companies, t('none')))
  }

  siretTyped() {
    clearTimeout(this.timer)
    const digits = this.siretTarget.value.replace(/\D/g, '')
    if (![9, 14].includes(digits.length)) return this.closePanel()

    this.timer = setTimeout(() => {
      this.request('search', { q: digits })
          .then(companies => this.showCandidates(companies.slice(0, 1), t('unknown_siret')))
    }, DEBOUNCE)
  }

  watchName() {
    const input = this.nameInput
    if (!input) return

    this.onNameInput   = () => this.suggest(input.value)
    this.onNameKeydown = event => this.navigate(event)
    input.addEventListener('input', this.onNameInput)
    input.addEventListener('keydown', this.onNameKeydown)
    input.setAttribute('autocomplete', 'off')

    this.menu = document.createElement('div')
    this.menu.className = 'company-lookup__menu'
    this.menu.hidden    = true
    input.parentNode.style.position = 'relative'
    input.insertAdjacentElement('afterend', this.menu)

    this.onOutsideClick = event => { if (!this.menu.contains(event.target) && event.target !== input) this.closeMenu() }
    document.addEventListener('click', this.onOutsideClick)
  }

  suggest(text) {
    clearTimeout(this.timer)
    const query = text.trim()
    if (query.length < 3 || /^\d[\d\s]*$/.test(query)) return this.closeMenu()

    this.timer = setTimeout(() => {
      this.request('search', { q: query }, { quiet: true }).then(companies => {
        if (this.nameInput.value.trim() !== query) return
        this.suggestions = companies
        this.active      = -1
        this.renderMenu()
      })
    }, DEBOUNCE)
  }

  renderMenu() {
    if (!this.suggestions?.length) return this.closeMenu()

    this.menu.innerHTML = `
      <div class="company-lookup__menu-title">${escapeHtml(t('directory'))}</div>
      ${this.suggestions.map((company, index) => `
        <button type="button" class="company-lookup__option ${index === this.active ? 'is-active' : ''}" data-index="${index}">
          <span class="company-lookup__option-name">${escapeHtml(company.name)}</span>
          <span class="company-lookup__option-meta">${escapeHtml([company.postal_code, company.city].filter(Boolean).join(' '))} · ${escapeHtml(formatSiret(company.siret))}</span>
          ${this.badges(company)}
        </button>`).join('')}`
    this.menu.querySelectorAll('[data-index]').forEach(button =>
      button.addEventListener('mousedown', event => {
        event.preventDefault()
        this.pick(this.suggestions[Number(button.dataset.index)], { name: true })
      }))
    this.menu.style.top = `${this.nameInput.offsetTop + this.nameInput.offsetHeight + 4}px`
    this.menu.hidden    = false
  }

  navigate(event) {
    if (this.menu.hidden || !this.suggestions?.length) return

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step  = event.key === 'ArrowDown' ? 1 : -1
      this.active = Math.min(Math.max(this.active + step, 0), this.suggestions.length - 1)
      this.renderMenu()
    } else if (event.key === 'Enter' && this.active >= 0) {
      event.preventDefault()
      this.pick(this.suggestions[this.active], { name: true })
    } else if (event.key === 'Escape') {
      event.stopPropagation()
      this.closeMenu()
    }
  }

  closeMenu() {
    if (this.menu) this.menu.hidden = true
  }

  showCandidates(companies, emptyMessage) {
    if (!companies) return
    if (companies.length === 0) return this.showPanel(`<div class="company-lookup__empty">${escapeHtml(emptyMessage)}</div>`)

    this.candidates = companies
    this.showPanel(companies.map((company, index) => `
      <div class="company-lookup__card ${company.active ? '' : 'is-closed'}">
        <div class="company-lookup__card-head">
          <span class="company-lookup__card-name">${escapeHtml(company.name)}</span>
          ${company.confidence ? `<span class="company-lookup__confidence is-${company.confidence}">${escapeHtml(t(`confidence.${company.confidence}`))}</span>` : ''}
        </div>
        <div class="company-lookup__card-meta">${escapeHtml(company.address)}</div>
        <div class="company-lookup__card-meta">SIRET ${escapeHtml(formatSiret(company.siret))}</div>
        ${this.duplicates(company)}
        <div class="company-lookup__card-foot">
          ${this.badges(company)}
          <button type="button" class="company-lookup__choose" data-index="${index}">${escapeHtml(t('choose'))}</button>
        </div>
      </div>`).join(''))

    this.panelTarget.querySelectorAll('[data-index]').forEach(button =>
      button.addEventListener('click', () => this.pick(this.candidates[Number(button.dataset.index)])))
  }

  badges(company) {
    // "2020-05-31" is read as midnight UTC: shown in local time, it is the 30th west of Greenwich.
    const closedOn = company.closed_on && new Date(company.closed_on).toLocaleDateString(I18n.locale, { timeZone: 'UTC' })
    const closed   = closedOn ? t('closed_on', { date: closedOn }) : t('closed')
    const badges = [
      { text: company.head_office ? t('head_office') : t('establishment') },
      !company.active     && { text: closed, tone: 'is-danger' },
      company.public_body && { text: t('public_body'), tone: 'is-info' },
    ].filter(Boolean)

    return `<span class="company-lookup__badges">${badges.map(badge =>
      `<span class="company-lookup__badge ${badge.tone || ''}">${escapeHtml(badge.text)}</span>`).join('')}</span>`
  }

  duplicates(company) {
    if (!company.duplicates?.length) return ''

    return `<div class="company-lookup__duplicate">${escapeHtml(t('duplicate'))} ${company.duplicates.map(record =>
      `<a href="${escapeHtml(record.url)}" target="_blank" rel="noopener">${escapeHtml(record.name)}</a>`).join(', ')}</div>`
  }

  showPanel(html) {
    this.panelTarget.innerHTML = `<button type="button" class="company-lookup__close" aria-label="${escapeHtml(t('close'))}">×</button>${html}`
    this.panelTarget.hidden    = false
    this.panelTarget.querySelector('.company-lookup__close').addEventListener('click', () => this.closePanel())
    this.panelTarget.querySelector('.company-lookup__undo')?.addEventListener('click', () => this.undo())
  }

  closePanel() {
    this.panelTarget.hidden = true
  }

  pick(company, { name = false } = {}) {
    this.closeMenu()
    this.snapshot  = this.currentValues()
    this.fillToken = null
    const filled = []

    if (this.nameInput && (name || this.replaceable(this.nameInput))) {
      this.write(this.nameInput, company.name)
      filled.push(this.nameInput)
    }

    if (this.hasSiretTarget) {
      this.siretTarget.value = company.siret
      filled.push(this.siretTarget)
    }

    this.vatInputs.forEach(input => {
      if (!this.replaceable(input)) return
      this.write(input, company.vat_number)
      if (input.type !== 'hidden') filled.push(input)
    })

    if (this.addressInput && this.replaceable(this.addressInput)) this.fillAddress(company)

    filled.forEach(input => this.flash(input))
    this.refresh()
    this.showPanel(`
      <div class="company-lookup__done">
        ${escapeHtml(t('filled'))}
        <button type="button" class="company-lookup__undo">${escapeHtml(t('undo'))}</button>
      </div>
      ${company.public_body ? `<div class="company-lookup__note">${escapeHtml(t('public_body_note'))}</div>` : ''}
      ${company.active ? '' : `<div class="company-lookup__duplicate">${escapeHtml(t('closed_warning'))}</div>`}
      ${this.duplicates(company)}`)
  }

  // Picking again must not leave the first company's VAT number beside the second one's SIRET.
  replaceable(input) {
    return !input.value.trim() || this.written.get(input) === input.value
  }

  write(input, value) {
    input.value = value || ''
    this.written.set(input, input.value)
  }

  currentValues() {
    return {
      name:    this.nameInput?.value || '',
      address: this.addressInput?.value || '',
      siret:   this.hasSiretTarget ? this.siretTarget.value : '',
      vats:    this.vatInputs.map(input => input.value),
    }
  }

  undo() {
    const before = this.snapshot
    if (!before) return

    if (this.nameInput) this.nameInput.value = before.name
    if (this.hasSiretTarget) this.siretTarget.value = before.siret
    this.vatInputs.forEach((input, index) => { input.value = before.vats[index] ?? '' })
    this.addressInput?.dispatchEvent(new CustomEvent('address:fill', { bubbles: true, detail: before.address }))

    this.snapshot  = null
    this.fillToken = null
    this.written.clear()
    this.closePanel()
    setTimeout(this.refresh, 50)
  }

  // The directory writes addresses in capitals; the suggestions give Yuman's format.
  fillAddress(company) {
    if (!company.address) return

    const token = this.fillToken = {}
    const url   = new URL('/geo/suggest', location.origin)
    url.searchParams.set('q', company.address)

    fetch(url, { headers: HEADERS, credentials: 'same-origin' })
      .then(response => response.ok ? response.json() : { suggestions: [] })
      .catch(() => ({ suggestions: [] }))
      .then(data => {
        if (token !== this.fillToken) return

        const match = (data.suggestions || []).find(suggestion => suggestion.postal_code === company.postal_code)
        const input = this.addressInput
        if (!input || !this.replaceable(input)) return

        const address = match ? match.address : company.address
        input.dispatchEvent(new CustomEvent('address:fill', { bubbles: true, detail: address }))
        this.written.set(input, address)
        setTimeout(() => { this.flash(input); this.refresh() }, 50)
      })
  }

  flash(input) {
    input.classList.remove('company-lookup--filled')
    void input.offsetWidth
    input.classList.add('company-lookup--filled')
  }

  request(action, params, { quiet = false } = {}) {
    const url = new URL(this.data.get(`${action}Url`), location.origin)
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value || ''))
    url.searchParams.set('kind', this.data.get('kind'))
    if (this.data.get('recordId')) url.searchParams.set('record_id', this.data.get('recordId'))

    if (!quiet) this.showPanel(`<div class="company-lookup__empty"><span class="spinner-sm"></span> ${escapeHtml(t('searching'))}</div>`)

    return fetch(url, { headers: HEADERS, credentials: 'same-origin' })
      .then(response => {
        if (!response.ok) throw new Error(response.status)
        return response.json()
      })
      .then(data => data.companies || [])
      .catch(() => {
        if (!quiet) this.showPanel(`<div class="company-lookup__empty">${escapeHtml(t('unavailable'))}</div>`)
        return null
      })
  }
}
